import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { BaseScreen } from '../../components/BaseScreen.tsx';
import ErrorMessage from '../../components/ErrorMessage.tsx';
import { Link } from '../../components/Link.tsx';
import OTP from '../../components/OTP.tsx';
import { useMergedConfig } from '../../context/MergedConfigProvider.tsx';
import { useMParticle } from '../../hooks/useMparticle.ts';
import { useTranslations } from '../../hooks/useTranslations.ts';
import { apiOtpResend, apiOtpVerify, ApiOtpVerifyResult } from '../../onboarding-api';
import { translationKeys } from '../../translations/translationKeys.ts';
import { getTranslatedApiError } from '../../utils/getTranslatedApiError.ts';
import { SignUpState } from '../SignUpJourney.tsx';

type Props = {
  signUpState: SignUpState;
  onGoBack: () => void;
  onCompleted: (requiresSetPassword: boolean, oneTimeToken?: string, connectApplicationId?: string) => void;
};

const RESEND_COUNTDOWN = 30;

function validateOtp(otp: string) {
  return otp.length === 6;
}

export const OtpScreen: FC<Props> = ({ signUpState, onGoBack, onCompleted }) => {
  const [otp, setOtp] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const otpIsValid = useMemo(() => {
    return validateOtp(otp);
  }, [otp]);
  const [resendCountdown, setResendCountdown] = useState<number>(0);
  const { journeyId } = useMergedConfig();
  const { translate } = useTranslations();

  const { setUserData, logRegistrationVerificationCode, logRegistrationVerified, logVerificationFailed } =
    useMParticle();

  const isDisabled = !otpIsValid || isSubmitting;

  useEffect(() => {
    logRegistrationVerificationCode();
  }, [logRegistrationVerificationCode]);

  useEffect(() => {
    if (resendCountdown) {
      const countdownInterval = setInterval(() => {
        setResendCountdown((currentState) => currentState - 1);
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [resendCountdown, setResendCountdown]);

  const onSubmit = useCallback(async () => {
    if (!isDisabled) {
      setError(null);
      setIsSubmitting(true);

      const { data, errorCode, errorMessage }: ApiOtpVerifyResult = await apiOtpVerify(
        otp,
        signUpState.csrfToken,
        journeyId
      );
      if (data) {
        if (data.p_id) {
          setUserData({ personId: data.p_id, email: signUpState.email });
        } else {
          setUserData({ email: signUpState.email });
        }
        logRegistrationVerified();

        onCompleted(data.requiresSetPassword || false, data.oneTimeToken, data.connectApplicationId);
      } else if (errorCode) {
        setError(getTranslatedApiError(errorCode, translate, errorMessage));
        logVerificationFailed();
      }

      setIsSubmitting(false);
    }
  }, [
    isDisabled,
    onCompleted,
    otp,
    setUserData,
    logRegistrationVerified,
    logVerificationFailed,
    signUpState.csrfToken,
    signUpState.email,
    journeyId,
  ]);

  const onResendOtp = useCallback(async () => {
    if (!isSubmitting) {
      setError(null);
      setIsSubmitting(true);

      const { errorCode, errorMessage } = await apiOtpResend(signUpState.csrfToken, journeyId);
      if (!errorCode) {
        setOtp('');
        setResendCountdown(RESEND_COUNTDOWN);
      } else {
        setError(getTranslatedApiError(errorCode, translate, errorMessage));
      }

      setIsSubmitting(false);
    }
  }, [isSubmitting, signUpState.csrfToken, journeyId]);

  return (
    <BaseScreen
      headerBackBtnVisible={true}
      headerBackBtnOnClick={onGoBack}
      headerBackBtnDisabled={isSubmitting}
      formTitle={translate(translationKeys.OTP_SCREEN_TITLE)}
      formSubtitle={`${translate(translationKeys.OTP_SCREEN_SUBTITLE)} ${signUpState.email}`}
      footerBtnLabel={translate(translationKeys.OTP_SCREEN_FOOTER_BUTTON_LABEL)}
      footerBtnOnClick={onSubmit}
      footerBtnLoading={isSubmitting}
      footerBtnDisabled={!otpIsValid}
    >
      <OTP
        // LM TODO We should be able to just pass the otp value rather than using the key trick to clear it
        key={resendCountdown === RESEND_COUNTDOWN ? new Date().getTime() : undefined}
        updateFieldValue={setOtp}
        triggerFieldChange={() => {}}
        disabled={isSubmitting}
        onSubmit={onSubmit}
      />

      {error && <ErrorMessage message={error} />}

      <div className={'flex justify-center text-sm'}>
        {resendCountdown > 0 ? (
          <p>
            {translate(translationKeys.OTP_SCREEN_RESEND_COUNTDOWN)} {resendCountdown}s
          </p>
        ) : (
          <p>
            {translate(translationKeys.OTP_SCREEN_RESEND_PROMPT)}{' '}
            <Link
              label={translate(translationKeys.OTP_SCREEN_RESEND_LABEL)}
              onClick={onResendOtp}
              disabled={isSubmitting}
            ></Link>
          </p>
        )}
      </div>
    </BaseScreen>
  );
};
